import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, ArrowLeft, BookOpen, Activity } from 'lucide-react';
import { endImpersonation, getOriginalParent } from '@/utils/parentImpersonation';

/**
 * Site-wide banner that renders when a parent is browsing the platform as
 * one of their children. Gives them quick navigation to common child pages
 * (Resources, Activities) and a one-click "Back to Parent Dashboard" exit.
 *
 * Mounted once at the App root so it persists across routes.
 */
const ImpersonationBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [original, setOriginal] = useState(null);

  // Recompute on every route change so a fresh impersonation start
  // (which happens after a navigate()) shows the banner immediately.
  useEffect(() => {
    setOriginal(getOriginalParent());
  }, [location.pathname]);

  if (!original) return null;

  const handleSwitchBack = () => {
    endImpersonation();
    // Hard navigate so App.js re-reads localStorage from scratch.
    window.location.replace('/parent-dashboard');
  };

  // Pull child name from the live user record (it was overwritten on swap).
  let childName = 'your child';
  try {
    const userRaw = localStorage.getItem('user');
    if (userRaw) {
      const u = JSON.parse(userRaw);
      childName = u.fullName || u.full_name || u.name || childName;
    }
  } catch {
    /* ignore */
  }

  return (
    <div
      className="sticky top-0 z-50 w-full border-b border-amber-400/30 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white shadow-lg"
      data-testid="parent-impersonation-banner"
      role="region"
      aria-label="Parent view-as-child banner"
    >
      <div className="max-w-7xl mx-auto px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex-shrink-0 bg-white/20 p-1.5 rounded-lg">
            <Eye className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold truncate">
              Viewing as <span data-testid="impersonation-child-name">{childName}</span>
            </p>
            <p className="text-xs text-white/85 hidden sm:block">
              You&apos;re browsing Edpies through your child&apos;s account. Any actions count for them, not you.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => navigate('/resources-hub')}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/15 hover:bg-white/25 text-xs font-medium transition-colors"
            data-testid="impersonation-go-resources"
          >
            <BookOpen className="h-3.5 w-3.5" />
            Resources
          </button>
          <button
            onClick={() => navigate('/categories')}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/15 hover:bg-white/25 text-xs font-medium transition-colors"
            data-testid="impersonation-go-activities"
          >
            <Activity className="h-3.5 w-3.5" />
            Activities
          </button>
          <button
            onClick={handleSwitchBack}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white text-amber-700 hover:bg-amber-50 text-xs font-semibold shadow"
            data-testid="impersonation-switch-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Parent Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
